import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, PageSignIn } from '@repo/zolintra-ui';
// Hooks
import { useAppContext } from '../../store/AppContext';
// Services
import { useOwnResourceQuery } from '../../services/resource/resourceQueries';
import { getAccessToken } from '../../services/api';
import {
  clearAuthCookie,
  isUserAuthenticated,
  parseJwt,
  redirectToCognitoLogin,
  redirectToCognitoLogout,
  setAuthCookie,
} from '../../utils/cognitoAuth';

function Login() {
  const [parsedJWT, setParsedJWT] = useState(null);
  const cognitoLoginRequestSent = useRef(false);
  const userPopulated = useRef(false);

  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');

  // Hooks
  const { t } = useTranslation();
  const [{ isAuthenticated, user }, dispatch] = useAppContext();
  // API Calls
  // Une indisponibilité d'identity-access n'est pas une erreur de programmation :
  // on la garde locale à l'écran de connexion plutôt que de la laisser remonter au
  // boundary du routeur, et on écrit nous-même le message plutôt qu'un toast.
  const ownResourceData = useOwnResourceQuery({
    enabled: parsedJWT !== null,
    throwOnError: false,
    meta: {
      preventToastError: true,
    },
  });

  useEffect(() => {
    if (!code && !isUserAuthenticated()) {
      redirectToCognitoLogin();
    }
  }, [code]);

  useEffect(() => {
    if (userPopulated.current || !ownResourceData.data || !parsedJWT) {
      return;
    }

    userPopulated.current = true;

    const { prm } = parsedJWT;
    const connectedUser = {
      ...ownResourceData.data,
      prm,
    };

    dispatch({ type: 'SET_AUTHENTICATION' });
    dispatch({ type: 'SET_USER', payload: connectedUser });
  }, [ownResourceData.data, parsedJWT, dispatch]);

  useEffect(() => {
    if (!code) {
      return;
    }

    if (cognitoLoginRequestSent.current) {
      return;
    }

    cognitoLoginRequestSent.current = true;

    const handleCognitoLogin = async () => {
      // Un code refusé par Cognito fait rejeter getAccessToken : on ramène ce cas
      // sur la même branche d'échec qu'une réponse sans token.
      const response = await getAccessToken(code).catch(() => undefined);

      if (response?.access_token) {
        const jwt = parseJwt(response.access_token);

        setAuthCookie({
          access_token: response.access_token,
          refresh_token: response.refresh_token,
        });

        setParsedJWT(jwt);
      } else {
        clearAuthCookie();
        // Passer par le logout Cognito ferme la session hosted UI : sans cela un
        // échange de code en échec relancerait indéfiniment le même callback.
        redirectToCognitoLogout();
      }
    };

    handleCognitoLogin();
  }, [code]);

  if (isAuthenticated && user?.id) {
    return <Navigate to="/planning" replace />;
  }

  if (code) {
    // Le code OAuth est à usage unique : on relance la requête plutôt que de
    // renvoyer vers Cognito, qui rejouerait un code déjà consommé.
    if (ownResourceData.isError) {
      return (
        <PageSignIn
          label={t('common.signInFailed')}
          isLoading={false}
          action={(
            <Button
              color="primary200"
              shape="rectangle"
              onClick={() => ownResourceData.refetch()}
            >
              {t('common.retry')}
            </Button>
          )}
        />
      );
    }

    return <PageSignIn label={t('common.signInPending')} isLoading action={null} />;
  }

  return null;
}

Login.propTypes = {};

export default Login;
